import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import toast from 'react-hot-toast';
import { useAuth } from './AuthContext';
import { notificationSound } from '../utils/notificationSound';
import { getSocketUrl } from '../config/env';

interface SocketContextValue {
  isConnected: boolean;
  socket: Socket | null;
}

const SocketContext = createContext<SocketContextValue>({ isConnected: false, socket: null });

export const useSocket = () => useContext(SocketContext);

interface SocketProviderProps {
  children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [socket, setSocket] = useState<Socket | null>(null);
  const { user, organization } = useAuth();

  const showBrowserNotification = useCallback((title: string, message: string, _icon?: string) => {
    // Check if browser supports notifications
    if (!('Notification' in window)) {
      return;
    }

    // Request permission if not granted
    if (Notification.permission === 'granted') {
      try {
        new Notification(title, {
          body: message,
          icon: '/logo192.png',
          badge: '/logo192.png',
          tag: 'dashboard-notification',
          requireInteraction: false,
        });
      } catch (error) {
        // Ignore notification errors
      }
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          try {
            new Notification(title, {
              body: message,
              icon: '/logo192.png',
              badge: '/logo192.png',
              tag: 'dashboard-notification',
              requireInteraction: false,
            });
          } catch (error) {
            // Ignore notification errors
          }
        }
      });
    }
  }, []);

  const showNotification = useCallback((data: any) => {
    const { type, title, message, action, notification } = data;
    const priority = notification?.priority || 'normal';
    
    // Get color and duration based on priority
    const getPriorityStyle = (priority: string) => {
      switch (priority) {
        case 'urgent':
          return {
            background: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)',
            color: '#fff',
            duration: 8000,
            icon: '🚨'
          };
        case 'high':
          return {
            background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
            color: '#fff',
            duration: 7000,
            icon: '⚠️'
          };
        case 'normal':
          return {
            background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
            color: '#fff',
            duration: 5000,
            icon: '🔔'
          };
        case 'low':
          return {
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            color: '#fff',
            duration: 4000,
            icon: '📌'
          };
        default:
          return {
            background: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
            color: '#fff',
            duration: 4000,
            icon: '🔔'
          };
      }
    };

    const priorityStyle = getPriorityStyle(priority);
    
    // Show toast notification based on type
    if (type === 'approval_required') {
      toast(message, {
        icon: priorityStyle.icon,
        duration: priorityStyle.duration,
        style: {
          background: priorityStyle.background,
          color: priorityStyle.color,
          fontWeight: priority === 'urgent' || priority === 'high' ? 'bold' : 'normal',
        },
      });
    } else if (type === 'approval_processed') {
      if (action === 'approved') {
        toast.success(message, {
          icon: '✅',
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            color: '#fff',
          },
        });
      } else if (action === 'rejected') {
        toast.error(message, {
          icon: '❌',
          duration: 6000,
          style: {
            background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
            color: '#fff',
          },
        });
      } else {
        toast(message, {
          icon: '🔔',
          duration: 5000,
        });
      }
    } else if (type === 'task_assignment') {
      toast(message, {
        icon: priorityStyle.icon,
        duration: priorityStyle.duration,
        style: {
          background: priorityStyle.background,
          color: priorityStyle.color,
          fontWeight: priority === 'urgent' || priority === 'high' ? 'bold' : 'normal',
        },
      });
    } else if (type === 'mention') {
      toast(message, {
        icon: '💬',
        duration: priorityStyle.duration,
        style: {
          background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
          color: '#fff',
        },
      });
    } else {
      // Default for any other notification type
      toast(message, {
        icon: priorityStyle.icon,
        duration: priorityStyle.duration,
        style: {
          background: priorityStyle.background,
          color: priorityStyle.color,
        },
      });
    }

    // Play notification sound based on type
    try {
      notificationSound.playSoundForType(type);
    } catch (error) {
      // Ignore audio errors
    }

    // Show browser notification if permission granted
    showBrowserNotification(title, message, priorityStyle.icon);
  }, [showBrowserNotification]);

  useEffect(() => {
    // Only connect if user is authenticated
    if (!user) {
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    // Socket.IO connects to API origin (see getSocketUrl), not the /api path.
    const socketUrl = getSocketUrl();
    if (!socketUrl) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[SocketProvider] No socket URL resolved; skipping Socket.IO connection.');
      }
      return;
    }

    const newSocket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    setSocket(newSocket);

    // Connection event handlers
    newSocket.on('connect', () => {
      // Socket.IO connected
      setIsConnected(true);

      // Join user-specific room
      if (user?.id) {
        newSocket.emit('join-user-room', user.id);
      }

      // Join organization room
      if (organization?.id) {
        newSocket.emit('join-organization', organization.id);
      }
    });

    newSocket.on('disconnect', () => {
      // Socket.IO disconnected
      setIsConnected(false);
    });

    newSocket.on('connect_error', (_error: Error) => {
      // Socket.IO connection error
      setIsConnected(false);
    });

    // Listen for notifications
    newSocket.on('notification', (data: any) => {
      // Real-time notification received
      showNotification(data);
      
      // Dispatch custom event for notification count refresh
      window.dispatchEvent(new CustomEvent('new-notification', { detail: data }));
    });

    // Cleanup on unmount
    return () => {
      newSocket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, organization, showNotification]);

  const value = useMemo(() => ({ isConnected, socket }), [isConnected, socket]);

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};  